import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import './index.css';

// Universal API request interceptor: ensure credentials and session tokens are consistently sent
try {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    const interceptedFetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      try {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request)?.url || '';
        const isApi = typeof url === 'string' && (url.startsWith('/api/') || url.includes('/api/'));

        if (isApi) {
          init = init || {};
          if (!init.credentials) {
            init.credentials = 'include';
          }

          // Safe storage access (prevents iframe SecurityError)
          let token: string | null = null;
          try {
            token = localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token');
          } catch (_) {}

          if (token && typeof token === 'string' && token !== 'undefined' && token !== 'null' && token.trim().length > 0) {
            // Do not attach token if explicitly logging out
            if (!url.includes('/api/auth/logout')) {
              const headers = new Headers(init.headers || (input instanceof Request ? input.headers : {}));
              if (!headers.has('Authorization')) {
                headers.set('Authorization', `Bearer ${token}`);
              }
              if (!headers.has('X-Session-Token')) {
                headers.set('X-Session-Token', token);
              }
              init.headers = headers;
            }
          }
        }
      } catch (err) {
        console.warn('[Fetch Interceptor] Non-blocking warning:', err);
      }

      return originalFetch(input as any, init);
    };

    // Safely assign or define without throwing in strict/sandboxed environments where window.fetch is getter-only
    try {
      (window as any).fetch = interceptedFetch;
    } catch (_) {
      try {
        Object.defineProperty(window, 'fetch', {
          value: interceptedFetch,
          writable: true,
          configurable: true,
        });
      } catch (defErr) {
        console.warn('[Fetch Interceptor] Native fetch is read-only in this host sandbox; using native fetch:', defErr);
      }
    }
  }
} catch (e) {
  console.warn('[Fetch Interceptor] Initialization skipped:', e);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Register PWA service worker in supporting browser environments
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((regError) => {
      console.warn('[PWA] Service worker registration notice:', regError);
    });
  });
}
